import { useEffect, useRef, useState, useCallback } from 'react'
import { apiGetLiveVisitors } from 'services/AnalyticsService'

/**
 * Polls the live-visitors endpoint and returns { onlineCount, visitors, loading }.
 *
 * Pass `onNewVisitor` to be called once per genuinely-new session (used for
 * toasts). The first poll only seeds the seen-set — it does NOT fire the
 * callback, so opening the dashboard doesn't blast a toast for every existing
 * visitor. Only sessions whose firstSeen is within the last 2 minutes toast.
 */
export default function useLiveVisitors({ intervalMs = 12000, onNewVisitor, onHighIntent } = {}) {
    const [onlineCount, setOnlineCount] = useState(0)
    const [visitors, setVisitors] = useState([])
    const [funnel, setFunnel] = useState({ browsing: 0, product: 0, checkout: 0, total: 0 })
    const [loading, setLoading] = useState(true)

    const seenRef = useRef(new Set())
    const highSeenRef = useRef(new Set()) // sessions we've already high-intent-alerted
    const firstLoadRef = useRef(true)
    const onNewRef = useRef(onNewVisitor)
    const onHighRef = useRef(onHighIntent)
    onNewRef.current = onNewVisitor
    onHighRef.current = onHighIntent

    const poll = useCallback(async () => {
        try {
            const res = await apiGetLiveVisitors()
            const data = res?.data?.data || res?.data || {}
            const list = Array.isArray(data.visitors) ? data.visitors : []

            setOnlineCount(data.onlineCount || 0)
            // Stable references: reuse the previous object for visitors whose
            // data hasn't changed so memoized rows skip rerendering and the
            // list doesn't visually churn on every poll.
            setVisitors((prev) => {
                const prevById = new Map(prev.map((p) => [p.id, p]))
                let anyChange = prev.length !== list.length
                const next = list.map((v, i) => {
                    const old = prevById.get(v.id)
                    if (old && JSON.stringify(old) === JSON.stringify(v)) {
                        if (prev[i] !== old) anyChange = true
                        return old
                    }
                    anyChange = true
                    return v
                })
                return anyChange ? next : prev
            })
            if (data.funnel) setFunnel(data.funnel)

            const isFirst = firstLoadRef.current
            const newCb = onNewRef.current
            const highCb = onHighRef.current
            for (const v of list) {
                if (!v?.id) continue
                // New-visitor toast (once per session, recent arrivals only).
                if (!seenRef.current.has(v.id)) {
                    seenRef.current.add(v.id)
                    if (!isFirst && newCb) {
                        const age = Date.now() - new Date(v.firstSeen).getTime()
                        if (age < 2 * 60 * 1000) newCb(v)
                    }
                }
                // High-intent alert when a session first reaches checkout / a
                // high-value item — fires for new AND existing sessions, so we
                // catch the moment a browser turns into a serious buyer.
                if ((v.intent === 'checkout' || v.intent === 'high_value') && !highSeenRef.current.has(v.id)) {
                    highSeenRef.current.add(v.id)
                    if (!isFirst && highCb) highCb(v)
                }
            }
            firstLoadRef.current = false
        } catch (e) {
            // Silent — the endpoint 403s for non-admins and we don't want noise.
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        poll()
        // Pause while the tab is hidden; refresh immediately on return.
        const t = setInterval(() => {
            if (document.visibilityState === 'visible') poll()
        }, intervalMs)
        const onVisibility = () => {
            if (document.visibilityState === 'visible') poll()
        }
        document.addEventListener('visibilitychange', onVisibility)
        return () => {
            clearInterval(t)
            document.removeEventListener('visibilitychange', onVisibility)
        }
    }, [poll, intervalMs])

    return { onlineCount, visitors, funnel, loading }
}
